import { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { CountdownTimer } from '../../CountdownTimer/CountdownTimer';
import { InfoBannerType } from '../../InfoBanner/InfoBanner';
import { MoveStatusRenderer } from '../../MoveStatusRenderer/MoveStatusRenderer';
import { MoveStatusType } from '../../MoveStatusRenderer/MoveStatusRenderer.types';

import { getTimeUntilNoOneJoinedStatus } from 'modules/Room/game-status/utils/init-game-statuses';

import { NO_PLAYER_FOUND_TIMEOUT } from 'shared/constants/timers';
import { useAccount } from 'shared/hooks/use-web3';
import { useRoomStore } from 'shared/store/roomStore';
import { networkHandler } from 'shared/utils/networkHandler';

import LinkSvg from 'assets/link.svg?react';

interface SearchingForOpponentProps {
  messages?: string[];
  className?: string;
  hash?: `0x${string}` | null;
  selector?: string;
  waitingToAccept?: boolean;
}

const SHOW_MESSAGES_INTERVAL = 1e4; // 10 seconds
export const SearchingForOpponent: FC<SearchingForOpponentProps> = ({
  messages,
  className,
  hash,
  selector,
  waitingToAccept,
}) => {
  const { chainId } = useAccount();
  const { t } = useTranslation();
  const [currentMessage, setCurrentMessage] = useState(0);
  const { closerRoom } = useRoomStore();
  const diff = getTimeUntilNoOneJoinedStatus(closerRoom);

  const shouldShowCountdown = diff + NO_PLAYER_FOUND_TIMEOUT > 0;

  useEffect(() => {
    if (messages.length === 0) return;

    const messageInterval = setInterval(() => {
      setCurrentMessage((prev) => (prev + 1) % messages.length);
    }, SHOW_MESSAGES_INTERVAL);

    return () => clearInterval(messageInterval);
  }, [messages]);

  const content = (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <div className="flex flex-col items-center justify-center">
        <MoveStatusRenderer
          hideIconOnMobile
          statusIcon={waitingToAccept ? MoveStatusType.WAITING_TO_ACCEPT : MoveStatusType.SEARCH}
          title={
            <>
              {hash && (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex justify-center text-[12px] text-white sm:text-[16px]"
                  href={`${networkHandler(chainId!)}/${hash}`}
                >
                  <LinkSvg className="mr-[10px] h-[18px] w-[16px] text-white sm:h-[22px] sm:w-[22px]" />
                  {t('common.transaction')}...{hash.slice(-4)}
                </a>
              )}
              <p>{t(messages[currentMessage])}</p>
            </>
          }
          type={waitingToAccept ? InfoBannerType.WAITING : InfoBannerType.INFO}
        />

        {shouldShowCountdown && (
          <CountdownTimer
            expiryTimestamp={new Date(Date.now() + diff + NO_PLAYER_FOUND_TIMEOUT)}
            description={t('textLoaders.lookingForOpponent.typicalTime')}
          />
        )}
      </div>
    </div>
  );

  if (selector) {
    const portalRoot = document.querySelector(selector);
    if (portalRoot) {
      return createPortal(content, portalRoot);
    }
  }

  return content;
};
